// src/modules/task-comments/CommentsSection.tsx
import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { formatDistanceToNow } from "date-fns";
import CommentForm from "./CommentForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReply,
  faEdit,
  faTrash,
  faTimes,
  faCheck,
  faPaperPlane,
  faArrowLeft,
  faSyncAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  postComment,
  searchComments,
  updateComment as apiUpdateComment,
  deleteComment as apiDeleteComment,
  CommentDTO,
  CommentFilterDTO,
  createCommentWithUploads,
} from "./CommentsAPI";

export type Comment = {
  id: string;
  author: {
    name: string;
    isAdmin?: boolean;
  };
  message: string;
  attachments?: string[];
  createdAt: string;
  isOwnComment: boolean;
  replies?: Comment[];
};

type CommentsSectionProps = {
  initialComments: Comment[];
  entityId: string;
  entityType: string;
};

/** ------- Permission helpers (reads from localStorage.user.permissions) ------- */
type PermissionDTO = {
  moduleId: string;
  moduleName: string; // e.g., "Comments"
  moduleDescription?: string;
  canCreate?: boolean;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canReport?: boolean;

  // Extended flags if present
  canAssign?: boolean;
  canComment?: boolean;
  canUpload?: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  canChangeStatus?: boolean;
  canArchive?: boolean;
  canRestore?: boolean;
  canExport?: boolean;
  canImport?: boolean;
  canShare?: boolean;

  visibleInSidebar?: boolean;
  visibleInDashboard?: boolean;
};

const getUserFromLS = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getModulePerm = (moduleName: string): PermissionDTO | undefined => {
  const u = getUserFromLS();
  const perms: PermissionDTO[] = u?.permissions || [];
  return perms.find(
    (p) => (p.moduleName || "").toLowerCase() === moduleName.toLowerCase()
  );
};

const hasAny = (perm: PermissionDTO | undefined, keys: (keyof PermissionDTO)[]): boolean =>
  !!perm && keys.some((k) => !!(perm as any)[k]);

/** Map action words to underlying permission fields */
const canViewComments = () => {
  const pm = getModulePerm("Comments");
  return hasAny(pm, ["canView", "canComment", "canCreate", "canEdit", "canDelete"]);
};
const canCreateComment = () => {
  const pm = getModulePerm("Comments");
  // Prefer canComment if present, otherwise fall back to canCreate
  return hasAny(pm, ["canComment", "canCreate"]);
};
const canEditCommentOwn = () => {
  const pm = getModulePerm("Comments");
  return hasAny(pm, ["canEdit", "canComment"]);
};
const canDeleteCommentOwn = () => {
  const pm = getModulePerm("Comments");
  return hasAny(pm, ["canDelete", "canComment"]);
};

/** ---------------- Component ---------------- */
const CommentsSection: React.FC<CommentsSectionProps> = ({
  initialComments,
  entityId,
  entityType,
}) => {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editText, setEditText] = useState<string>("");
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [replyText, setReplyText] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const user = getUserFromLS();
  const tenantId = user?.tenantId || "";
  const tenantName = user?.tenantName || "";
  const currentUserId = user?.id || "";
  const username = user?.username || "Current User";
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes("Admin");

  const _canView = canViewComments();
  const _canCreate = canCreateComment();
  const _canEditOwn = canEditCommentOwn();
  const _canDeleteOwn = canDeleteCommentOwn();
  const _canEditAny = isAdmin;   // Admin can edit all
  const _canDeleteAny = isAdmin; // Admin can delete all

  /** Backend reload with the same filters used at page load */
  const refreshComments = async () => {
    try {
      setLoading(true);
      const filter: CommentFilterDTO = { entityType, entityId };
      const response = await searchComments(filter, 0, 100, "createdAt,desc");
      const data = response?.data?.content || response?.data || [];
      const mapped = (data as CommentDTO[]).map(mapDtoToComment);
      setComments(mapped);
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial sync from server (optional — if initialComments is stale)
    refreshComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  /** Map CommentDTO -> UI Comment */
  const mapDtoToComment = (dto: CommentDTO): Comment => ({
    id: dto.id || "",
    author: { name: dto.username ?? "Unknown" },
    message: dto.message ?? "",
    createdAt: dto.createdAt ?? new Date().toISOString(),
    isOwnComment: (dto.userId || "") === currentUserId,
    replies: (dto.replies || []).map(mapDtoToComment),
    attachments: dto.contentUrl ? [dto.contentUrl] : [],
  });

  /** CREATE or REPLY, then reload from server */
  const addComment = async (
    input: { message: string; attachments: File[] },
    parentId: string | null = null
  ) => {
    if (!_canCreate) {
      alert("You do not have permission to add comments.");
      return;
    }

    const dto: CommentDTO = {
      // Let server generate id; don't pass a client UUID to avoid mismatch
      id: undefined,
      entityId,
      tenantId,
      tenantName,
      userId: currentUserId,
      username,
      entityType,
      parentCommentId: parentId || undefined,
      message: input.message,
      messageType: input.attachments.length > 0 ? "FILE" : "TEXT",
      contentUrl: undefined,
      isDeleted: false,
    };

    try {
      if (input.attachments.length > 0) {
        await createCommentWithUploads(dto, input.attachments);
      } else {
        await postComment(dto);
      }
      // Always reload from backend so we have the real server id
      await refreshComments();
      setReplyingTo(null);
      setReplyText("");
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  /** EDIT, then reload */
  const updateComment = async (updatedComment: Comment) => {
    const isOwner = updatedComment?.author?.name === username; // best-effort; prefer comparing IDs if available in DTO
    if (!(_canEditAny || (_canEditOwn && isOwner))) {
      alert("You do not have permission to edit this comment.");
      return;
    }

    try {
      await apiUpdateComment(updatedComment.id, {
        id: updatedComment.id,
        entityId,
        entityType,
        userId: currentUserId,
        username,
        tenantId,
        tenantName,
        message: updatedComment.message,
        messageType: "TEXT",
      });

      await refreshComments();
      setEditingComment(null);
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  /** DELETE, then reload */
  const deleteComment = async (commentId: string, ownerUserName?: string) => {
    const isOwner = ownerUserName === username;
    if (!(_canDeleteAny || (_canDeleteOwn && isOwner))) {
      alert("You do not have permission to delete this comment.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this comment?")) return;

    try {
      await apiDeleteComment(commentId);
      await refreshComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleEditClick = (comment: Comment) => {
    setEditingComment(comment);
    setEditText(comment.message.replace(" (edited)", ""));
  };

  const renderComment = (comment: Comment, level = 0) => {
    const ownerName = comment?.author?.name;
    const isOwner = ownerName === username;

    // Decide action visibility
    const showEdit = _canEditAny || (_canEditOwn && isOwner);
    const showDelete = _canDeleteAny || (_canDeleteOwn && isOwner);
    const showReply = _canCreate; // allow reply if can create

    return (
      <div
        key={comment.id}
        // Tailwind can't parse dynamic classNames like pl-${}; use inline style
        style={{ paddingLeft: level * 16 }}
        className="border-l border-gray-200"
      >
        <div className="flex items-start space-x-2 mb-2">
          {/* If you have avatars, place here */}
          <div className="flex-1">
            <div className="flex justify-between items-center">
              <span className="font-semibold">{ownerName}</span>
              <span className="text-xs text-gray-400">
                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              </span>
            </div>

            <p className="mt-1 text-gray-800 whitespace-pre-wrap">{comment.message}</p>

            {(comment.attachments?.length ?? 0) > 0 && (
              <div className="mt-2 space-x-2">
                {(comment.attachments ?? []).map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-20 h-20 object-cover rounded border"
                  >
                    <img src={url} alt={`Attachment ${i}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}

            {_canView && (
              <div className="mt-2 text-sm text-gray-500 flex gap-4">
                {showReply && (
                  <button
                    className="hover:text-blue-600"
                    onClick={() => setReplyingTo(comment)}
                    title="Reply"
                  >
                    <FontAwesomeIcon icon={faReply} className="mr-1" />
                    Reply
                  </button>
                )}

                {showEdit && (
                  <button
                    className="hover:text-green-600"
                    onClick={() => handleEditClick(comment)}
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-1" />
                    Edit
                  </button>
                )}

                {showDelete && (
                  <button
                    className="hover:text-red-600"
                    onClick={() => deleteComment(comment.id, ownerName)}
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {comment.replies?.map((reply) => renderComment(reply, level + 1))}
      </div>
    );
  };

  return (
    <div className="bg-white p-4 rounded shadow-md space-y-4 relative">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Comments</h2>
        <div className="flex gap-2">
          <button
            onClick={() => window.history.back()}
            className="px-2 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>
          <button
            onClick={() => refreshComments()}
            className="px-2 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faSyncAlt} />
            Refresh
          </button>
        </div>
      </div>

      {/* Create new top-level comment */}
      {_canCreate ? (
        <CommentForm onSubmit={(data) => addComment(data)} />
      ) : (
        <div className="p-3 border rounded bg-gray-50 text-gray-600 text-sm">
          You don’t have permission to add comments.
        </div>
      )}

      {/* Loaded comments */}
      {!_canView ? (
        <div className="text-red-600">You do not have permission to view comments.</div>
      ) : loading ? (
        <div className="text-slate-500">Loading comments…</div>
      ) : (
        <div className="space-y-4">{comments.map((comment) => renderComment(comment))}</div>
      )}

      {/* Edit modal */}
      {editingComment && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Edit Comment</h3>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className="w-full p-2 border rounded resize-none"
              placeholder="Edit your comment"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="text-red-500 hover:text-red-700 flex items-center gap-1"
                onClick={() => setEditingComment(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
                Cancel
              </button>
              <button
                className="text-green-600 hover:text-green-800 flex items-center gap-1"
                onClick={() =>
                  updateComment({
                    ...editingComment,
                    message: editText.trim() + " (edited)",
                  })
                }
              >
                <FontAwesomeIcon icon={faCheck} />
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply modal */}
      {replyingTo && _canCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Replying to {replyingTo.author.name}</h3>
            <div className="p-3 text-sm text-gray-700 bg-gray-100 rounded mb-3">
              {replyingTo.message}
            </div>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              className="w-full p-2 border rounded resize-none"
              placeholder="Write your reply..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="text-red-500 hover:text-red-700 flex items-center gap-1"
                onClick={() => {
                  setReplyingTo(null);
                  setReplyText("");
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
                Cancel
              </button>
              <button
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                onClick={() => {
                  if (replyText.trim()) {
                    addComment({ message: replyText.trim(), attachments: [] }, replyingTo.id);
                    // addComment handles clearing modal state after success
                  }
                }}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Comment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsSection;